import { useMutation } from '@tanstack/react-query';
import * as z from 'zod';

import { loginWithEmailAndPassword } from 'src/features/auth/api/useLogin';
import { handleUserResponse } from 'src/features/auth/lib';
import axios from 'src/lib/axios';
import { type MutationConfig, queryClient } from 'src/lib/react-query';
import { validationMessagesMap } from 'src/utils/messages';
import { formatResourcePath } from 'src/utils/regex';

import type { RegisterResponse, User } from 'src/features/auth/types';

const RESOURCE = '/register/';

const formSchema = z
    .object({
        first_name: z.string().min(3, { message: validationMessagesMap.validationErrorFirstName }).max(24),
        last_name: z.string().min(3, { message: validationMessagesMap.validationErrorLastName }).max(24),
        email: z.string().email({ message: validationMessagesMap.validationErrorEmail }),
        password: z
            .string()
            .regex(new RegExp(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[#@$!%*?&_-])[a-zA-Z\d#@$!%*?&_-]{8,}$/), {
                message: validationMessagesMap.validationErrorPassword,
            }),
        confirm_password: z.string(),
    })
    // Confirm passwords match
    .superRefine(({ password, confirm_password }, ctx) => {
        if (password || confirm_password) {
            if (password !== confirm_password) {
                ctx.addIssue({
                    path: ['confirm_password'],
                    code: z.ZodIssueCode.custom,
                    message: validationMessagesMap.validationErrorConfirmPassword,
                });
            }
        }
    });
type FormSchemaValues = z.infer<typeof formSchema>;

interface DTO extends Omit<FormSchemaValues, 'confirm_password'> {
    invitation_id?: string;
    parking_id?: string;
}

type MutationFnType = (data: DTO) => Promise<User>;
const mutationFn = (data: DTO): Promise<RegisterResponse> => {
    return axios.post(formatResourcePath(RESOURCE), data);
};

// Calls the register endpoint, after successful registration, logs the user in and
// sets the user in the queryClient and tokens in the local storage
interface UseMutationOptions extends MutationConfig<MutationFnType> {}
const useMutationHook = (options: UseMutationOptions = {}) =>
    useMutation({
        mutationFn: async (data: DTO) => {
            await mutationFn(data);
            const tokenResponse = await loginWithEmailAndPassword({
                email: data.email,
                password: data.password,
            });
            const user = handleUserResponse(tokenResponse);
            return user;
        },
        ...options,
        onSuccess: (user, ...rest) => {
            queryClient.setQueryData(['authenticated-user'], user);
            options?.onSuccess?.(user, ...rest);
        },
    });

export {
    RESOURCE as REGISTER_RESOURCE,
    formSchema as registerFormSchema,
    mutationFn as registerWithEmailAndPassword,
    useMutationHook as useRegister,
};
export type { DTO as RegisterDTO, FormSchemaValues as RegisterPayload };
